import { Utils } from 'jssip';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  sendAnalyticsError,
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  callIdSelector,
  sipCallIdSelector,
} from 'features/call/call-base/store/selectors';
import { selectCallPageStatus } from 'features/call/call-ui-state/store';
import {
  isRecoverableLocalVideoError,
  useLocalVideo,
  useTrackGuard,
} from 'features/local-video/hooks';
import { useMediaStream } from 'features/media-streams/hooks';
import { getNetworkStats, isRtcConnectionActive } from 'features/sip/helpers';
import { useSipSession } from 'features/sip/hooks';
import { userSelector } from 'features/user/store';
import {
  isLocalVideoPrivacyEnabledSelector,
  startLocalVideoPrivacyLoading,
  stopLocalVideoPrivacyLoading,
} from 'features/video-privacy/store';

export interface UseLocalVideoManagerReturn {
  selfViewStream: MediaStream | null;
  handleStalled: () => Promise<void>;
  isPrivacyEnabled: boolean;
}

const STALLED_DEBOUNCE_MS = 5_000;

/**
 * Manages the full local-video lifecycle: self-view stream acquisition,
 * privacy-toggle orchestration, track guard integration, and stalled-video
 * error reporting.
 *
 * @returns {UseLocalVideoManagerReturn} An object containing:
 * - selfViewStream: The local self-view media stream used for rendering the user's video locally, or null when no stream is available.
 * - handleStalled: An async handler that should be called when the local video appears stalled to trigger debounced diagnostics and error reporting.
 * - isPrivacyEnabled: A boolean indicating whether local video privacy mode is currently enabled for the user.
 */
export const useLocalVideoManager = (): UseLocalVideoManagerReturn => {
  const dispatch = useAppDispatch();
  const rtcConnection = useSipSession();
  const selfViewStreamRef = useRef<MediaStream | null>(null);
  const lastStalledRef = useRef<number>(0);
  const [selfViewStream, setSelfViewStream] = useState<MediaStream | null>(
    null
  );

  const { getInterpVideoStream, getFreshInterpVideoStream, getSelfViewStream } =
    useMediaStream();
  const { sendVideoTrackToRemote, sendPrivacyTrackToRemote } = useLocalVideo();

  const isLocalVideoPrivacyEnabled = useAppSelector(
    isLocalVideoPrivacyEnabledSelector
  );
  const user = useAppSelector(userSelector);
  const callPageStatus = useAppSelector(selectCallPageStatus);
  const callId = useAppSelector(callIdSelector);
  const sipCallId = useAppSelector(sipCallIdSelector);

  const reportLocalVideoToggleError = useCallback(
    (error: unknown) => {
      console.error('Failed to toggle local video:', error);
      dispatch(
        sendAnalyticsError({
          name: 'Local Video Toggle Error',
          event: { error: String(error), callId, sipCallId },
        })
      );
    },
    [callId, dispatch, sipCallId]
  );

  useEffect(() => {
    let cancelled = false;

    if (selfViewStreamRef.current) return;

    (async () => {
      try {
        const videoStream = await getSelfViewStream();
        if (cancelled) {
          Utils.closeMediaStream(videoStream);
        } else {
          selfViewStreamRef.current = videoStream;
          setSelfViewStream(videoStream);
        }
      } catch (error) {
        console.error('Failed to acquire self-view stream:', error);
        dispatch(
          sendAnalyticsError({
            Method: 'useLocalVideoManager - getSelfViewStream',
            Event: { error: String(error) },
          })
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getSelfViewStream, dispatch]);

  useEffect(() => {
    return () => {
      if (selfViewStreamRef.current) {
        Utils.closeMediaStream(selfViewStreamRef.current);
        selfViewStreamRef.current = null;
      }
    };
  }, []);

  const handleLocalVideo = useCallback(async () => {
    if (!rtcConnection || !isRtcConnectionActive(rtcConnection)) return;

    dispatch(startLocalVideoPrivacyLoading());

    try {
      if (isLocalVideoPrivacyEnabled) {
        await sendPrivacyTrackToRemote(rtcConnection);
        return;
      }

      const streamToUse = await getInterpVideoStream();

      try {
        await sendVideoTrackToRemote(rtcConnection, streamToUse);
      } catch (error) {
        if (!isRecoverableLocalVideoError(error)) {
          reportLocalVideoToggleError(error);
          return;
        }

        dispatch(
          sendAnalyticsInfo({
            Method: 'useLocalVideoManager',
            Message: 'Retrying local video send with fresh interpreter stream',
            ErrorCode: error.message,
            CallId: callId,
            SipCallId: sipCallId,
          })
        );

        const freshStream = await getFreshInterpVideoStream();
        await sendVideoTrackToRemote(rtcConnection, freshStream);

        const freshVideoTrack = freshStream.getVideoTracks()[0];
        dispatch(
          sendAnalyticsInfo({
            Method: 'useLocalVideoManager',
            Message:
              'Fresh interpreter stream sent successfully after recoverable local video retry',
            TrackId: freshVideoTrack?.id,
            TrackReadyState: freshVideoTrack?.readyState,
            TrackEnabled: freshVideoTrack?.enabled,
            CallId: callId,
            SipCallId: sipCallId,
          })
        );
      }
    } catch (error) {
      reportLocalVideoToggleError(error);
    } finally {
      dispatch(stopLocalVideoPrivacyLoading());
    }
  }, [
    dispatch,
    reportLocalVideoToggleError,
    isLocalVideoPrivacyEnabled,
    rtcConnection,
    sendPrivacyTrackToRemote,
    getInterpVideoStream,
    getFreshInterpVideoStream,
    sendVideoTrackToRemote,
    callId,
    sipCallId,
  ]);

  const handleStalled = useCallback(async () => {
    const now = Date.now();
    if (now - lastStalledRef.current < STALLED_DEBOUNCE_MS) return;
    lastStalledRef.current = now;

    dispatch(
      sendAnalyticsWarning({
        name: 'Local Video Stalled',
        event: {
          user,
          callId,
          sipCallId,
          callPageStatus,
          networkStats: await getNetworkStats(rtcConnection),
        },
      })
    );
  }, [callId, callPageStatus, dispatch, rtcConnection, sipCallId, user]);

  useTrackGuard(rtcConnection, isLocalVideoPrivacyEnabled);

  useEffect(() => {
    handleLocalVideo();
  }, [handleLocalVideo]);

  return {
    selfViewStream,
    handleStalled,
    isPrivacyEnabled: isLocalVideoPrivacyEnabled,
  };
};
